import {
  BookOpen,
  Code2,
  Layers,
  Palette,
  ShieldCheck,
  Waypoints,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { TeamOrbitDiagram } from '@/features/agents-marketing/components/TeamOrbitDiagram'
import { specialistRoster } from '@/features/agents-marketing/data/agentsContent'
import { SectionHeading } from '@/features/home/components/SectionHeading'
import { Reveal } from '@/shared/components/Reveal'

type RoleName = (typeof specialistRoster)[number]['name']

function roleIcon(name: RoleName): LucideIcon {
  switch (name) {
    case 'Senior Architect':
      return Waypoints
    case 'Researcher':
      return BookOpen
    case 'Coder':
      return Code2
    case 'Reviewer':
      return ShieldCheck
    case 'UI/UX Consultant':
      return Palette
    case 'Front-end Consultant':
      return Layers
  }
}

export function TeamAnatomySection() {
  return (
    <section className="relative border-y border-[var(--color-border)] bg-[var(--color-bg-secondary)]/40 px-6 py-24 md:py-36">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <SectionHeading
            eyebrow="Team anatomy"
            title="One coordinator, and the specialists it brings in."
            lede="Every team has exactly one coordinator. It answers routine requests on its own and consults a specialist when a request needs one, up to five rounds per reply."
          />
        </Reveal>

        <div className="mt-16 grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <figure>
              <TeamOrbitDiagram label="One large coordinator node linked to three smaller specialist nodes. Two links return to the coordinator, one continues straight on to the right." />
              <figcaption className="mt-4 text-caption text-[var(--color-text-tertiary)]">
                Advice comes back to the coordinator. A delegated reply goes
                straight through.
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <h3 className="text-title-sm font-semibold tracking-[-0.02em] text-[var(--color-text-primary)]">
              Six specialists to choose from
            </h3>
            <p className="mt-2 text-body-sm leading-relaxed text-[var(--color-text-secondary)]">
              Each role can be added once, on any model in the catalog.
            </p>
            <ul className="mt-8 grid grid-cols-1 gap-x-8 border-t border-[var(--color-border)] sm:grid-cols-2">
              {specialistRoster.map((role) => {
                const Icon = roleIcon(role.name)
                return (
                  <li key={role.name} className="border-b border-[var(--color-border)] py-5">
                    <p className="flex items-center gap-2 text-body-sm font-medium text-[var(--color-text-primary)]">
                      <Icon
                        aria-hidden
                        className="size-3.5 shrink-0 text-[var(--color-text-tertiary)]"
                        strokeWidth={1.75}
                      />
                      {role.name}
                    </p>
                    <p className="mt-1.5 text-caption leading-relaxed text-[var(--color-text-secondary)]">
                      {role.useFor}
                    </p>
                  </li>
                )
              })}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
